/**
 * FocusPostView — "Lights Out" deep reading mode
 *
 * Full-screen overlay with dimmed background, large typography post body,
 * and MarginNoteSidebar on the right for annotations.
 *
 * Triggered by:
 *   - "Read Deeply" from NumaPick
 *   - Clicking any post in Bento Dashboard
 */

import { useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { ClubTheme } from './themes';
import type { SocialPost } from './types';
import { useAnnotations } from './queries';
import MarginNoteSidebar from './MarginNoteSidebar';
import ReactionBar from './ReactionBar';

interface FocusPostViewProps {
  post: SocialPost;
  clubId: string;
  theme: ClubTheme;
  onClose: () => void;
}

const ANNOTATION_COLORS: Record<string, string> = {
  note: '#FBBF2440',
  'fact-check': '#10B98130',
  question: '#3B82F630',
};

export default function FocusPostView({ post, clubId, theme, onClose }: FocusPostViewProps) {
  const { data: annotations = [] } = useAnnotations(clubId, post.id);

  // ESC to close
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  const authorName = post.author?.full_name || 'Unknown';
  const avatarUrl = post.author?.avatar_url;
  const body = post.body || '';
  const totalReactions = Object.values(post.reactions || {}).reduce((a, b) => a + b, 0);

  // Build highlighted body with annotation marks
  const renderedBody = buildAnnotatedBody(body, annotations);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
      style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Content container */}
      <div
        className="relative mx-4 my-8 flex w-full max-w-5xl rounded-2xl shadow-2xl"
        style={{ backgroundColor: theme.colors.bg }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 rounded-lg p-2 transition-colors hover:opacity-70"
          style={{ color: theme.colors.muted }}
          aria-label="Close focus mode"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        {/* Left: Post body (~60%) */}
        <div className="flex-1 p-8 md:p-12 overflow-y-auto max-h-[85vh]">
          {/* Author header */}
          <div className="flex items-center gap-3 mb-6">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
            ) : (
              <div
                className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold"
                style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
              >
                {(authorName[0] || '?').toUpperCase()}
              </div>
            )}
            <div>
              <span className="text-sm font-semibold" style={{ color: theme.colors.text }}>
                {authorName}
              </span>
              <div className="flex items-center gap-2 text-[11px]" style={{ color: theme.colors.muted }}>
                {post.channel && <span>{post.channel.name}</span>}
                <span>
                  {new Date(post.created_at).toLocaleDateString(undefined, {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Post body — large, readable typography */}
          <div
            className="prose prose-lg max-w-none leading-relaxed"
            style={{
              color: theme.colors.text,
              fontSize: '1.125rem',
              lineHeight: '1.8',
            }}
          >
            {renderedBody}
          </div>

          {/* Engagement footer */}
          <div className="mt-8 pt-4" style={{ borderTop: `1px solid ${theme.colors.muted}20` }}>
            <div className="flex items-center gap-4 text-xs" style={{ color: theme.colors.muted }}>
              {totalReactions > 0 && (
                <span>{totalReactions} reaction{totalReactions !== 1 ? 's' : ''}</span>
              )}
              {post.comment_count > 0 && (
                <span>{post.comment_count} comment{post.comment_count !== 1 ? 's' : ''}</span>
              )}
              {annotations.length > 0 && (
                <span>{annotations.length} annotation{annotations.length !== 1 ? 's' : ''}</span>
              )}
            </div>
          </div>
        </div>

        {/* Right: Margin Notes (~40%) — hidden on mobile */}
        <aside
          className="hidden md:block w-[340px] shrink-0 border-l overflow-y-auto max-h-[85vh]"
          style={{ borderColor: `${theme.colors.muted}15`, backgroundColor: `${theme.colors.surface}` }}
        >
          <MarginNoteSidebar annotations={annotations} theme={theme} />
        </aside>
      </div>
    </div>
  );
}

// ─── Build annotated body with highlight marks ────────────────────

function buildAnnotatedBody(
  text: string,
  annotations: Array<{ start_offset: number; end_offset: number; annotation_type: string }>,
): React.ReactNode[] {
  if (annotations.length === 0) {
    return text.split('\n').map((line, i) => (
      <p key={i} className={line.trim() === '' ? 'h-4' : ''}>
        {line || '\u00A0'}
      </p>
    ));
  }

  // Build segments
  const boundaries = new Set<number>();
  boundaries.add(0);
  boundaries.add(text.length);
  for (const a of annotations) {
    boundaries.add(Math.max(0, a.start_offset));
    boundaries.add(Math.min(text.length, a.end_offset));
  }

  const sorted = Array.from(boundaries).sort((a, b) => a - b);
  const elements: React.ReactNode[] = [];

  for (let i = 0; i < sorted.length - 1; i++) {
    const start = sorted[i];
    const end = sorted[i + 1];
    const segment = text.slice(start, end);
    const overlapping = annotations.filter(
      (a) => a.start_offset <= start && a.end_offset >= end,
    );

    if (overlapping.length > 0) {
      const color = ANNOTATION_COLORS[overlapping[0].annotation_type] || ANNOTATION_COLORS.note;
      elements.push(
        <mark
          key={`${start}-${end}`}
          style={{ backgroundColor: color, borderRadius: '2px', padding: '0 1px' }}
        >
          {segment}
        </mark>,
      );
    } else {
      elements.push(<span key={`${start}-${end}`}>{segment}</span>);
    }
  }

  return [<p key="body">{elements}</p>];
}
